import {
  DateTimeField,
  NumberField,
  RelationshipField,
  SelectField,
  StringField,
  LongTextField,
} from "../plugins/Cruxtify/field-types";
import {
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import { ApcCrudModel } from "./ApcCrudModel";

export class CompatibilityRule extends ApcCrudModel {
  public static icon = "mdi-rule";
  protected static _typeLabel = "Compatibility Rule";
  protected static asProperty = "compatibility_rule";
  public static api = {
    path: "compatibility-rules",
  };

  protected static routeBase = "compatibility-rules";

  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.Read,
    },
    UserPermission.New
  );

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "name",
        label: "Name",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "first_option",
        label: "First Option",
        relatedModel: "Option",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "first_option_group",
        label: "First Option Group",
        relatedModel: "OptionGroup",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "second_option",
        label: "Second Option",
        relatedModel: "Option",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "second_option_group",
        label: "Second Option Group",
        relatedModel: "OptionGroup",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "rule_type",
        label: "Rule Type",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "description",
        label: "Description",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: DateTimeField, opts: { property: "created_at" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: StringField, opts: { property: "name", isPrimaryLabelField: 1 } },
    {
      type: RelationshipField,
      opts: {
        property: "first_option",
        description:
          "Only use an option or and option group for the first rule member; not both.",
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "first_option_group",
        description:
          "Only use an option or and option group for the first rule member; not both.",
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "second_option",
        description:
          "Only use an option or and option group for the second rule member; not both.",
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "second_option_group",
        description:
          "Only use an option or and option group for the second rule member; not both.",
      },
    },
    {
      type: SelectField,
      opts: {
        property: "rule_type",
        description:
          "SUGGESTED: Automatically adds the second option when first is selected. \nINCOMPATIBLE: Prevents selecting both options together. \nREQUIRED_FIRST: The second option must be selected before the first can be added. \nREQUIRED: Automatically adds the second option when first is selected, and prevents removing it.",
        options: {
          required_first: "Required First",
          required: "Required",
          incompatible: "Incompatible",
          suggested: "Suggested",
        },
      },
    },
    {
      type: LongTextField,
      opts: {
        property: "description",
        description:
          "This message that will be displayed to the customer when the compatibility rule is enforced",
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "name",
        "rule_type",
        "first_option",
        "first_option_group",
        "second_option",
        "second_option_group",
        "description",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      useModal: true,
      dynamicFilterOptions: {
        filterFields: [
          "name",
          "rule_type",
          "first_option",
          "first_option_group",
          "second_option",
          "second_option_group",
          "description",
          "created_at",
          "updated_at",
        ],
      },
      defaultSort: [
        {
          field: "rule_type",
          order: "asc",
        },
      ],
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "ruleDetails",
        fieldSets: [
          {
            id: "ruleType",
            fields: ["name", "rule_type", "description"],
          },
          {
            id: "firstItem",
            label: "First Item",
            fields: ["first_option", "first_option_group"],
          },
          {
            id: "secondItem",
            label: "Second Item",
            fields: ["second_option", "second_option_group"],
          },
          {
            id: "meta",
            fields: ["created_at", "updated_at", "id"],
          },
        ],
      },
    },
  ];
}
